import React, { useCallback, useEffect, useRef, useState } from 'react'
import NetworkView, { NetworkView3Ref } from './networkView/NetworkView'
import { PersonType, SDManager } from './networkView/SDManager'
import { useManageRoleStore } from './manageRole/stateStore/manageRoleStore'

type Props = {
    ephemeralSeedIds?: string[]
}

function RoleNetworkView({ ephemeralSeedIds = [] }: Props): React.ReactNode {
    const networkViewRef = useRef<NetworkView3Ref>(null)
    const networkViewInitialised = useRef(false)
    const [sdManager, setSdManager] = useState<SDManager | null>(null)
    // const [networkSelectedPerson, setNetworkSelectedPerson] = useState<
    //     PersonDto | undefined
    // >(undefined)
    // const [seedPersonIds, setSeedPersonIds] = useState(new SetExtra<string>())
    // you MUST deconstruct these because they need to be dependencies on the useEffect
    //  if you try to add manageRoleState as a dependency it will cause an infinite loop!
    // const { candidates } = state
    const candidates = useManageRoleStore((state) => state.candidates)
    const selectCandidateId = useManageRoleStore(
        (state) => state.selectCandidateId,
    )
    const setRightPaneMode = useManageRoleStore(
        (state) => state.setRightPaneMode,
    )
    const safeGetRoleLite = useManageRoleStore((state) => state.safeGetRoleLite)
    const personLiteCache = useManageRoleStore().getState().personLiteCache
    const companyCache = useManageRoleStore().getState().companyCache
    const onCandidateChanged =
        useManageRoleStore().getState().onCandidateChanged
    const previousCandidatesLengthRef = useRef<number>(0)

    // function safeGetSdManager(): SDManager {
    //     if (!networkViewRef.current || !networkViewRef.current.sdManager) {
    //         throw new Error('SDManager not available')
    //     }
    //     return networkViewRef.current.sdManager
    // }

    // Callback ref to update when NetworkView mounts and exposes sdManager.
    const handleNetworkViewRef = useCallback(
        (node: NetworkView3Ref | null) => {
            ;(
                networkViewRef as React.MutableRefObject<NetworkView3Ref | null>
            ).current = node
            if (node && node.sdManager && !sdManager) {
                setSdManager(node.sdManager)
            }
        },
        [sdManager],
    )

    useEffect(() => {
        // fetch the hiring team PersonDtos for the role (including their trustedPersonIds) (BLUE BADGE)
        // fetch the trusted personDtos for those HMs (de-duplicate before sending) (GREEN BADGE)
        // fetch all the people who have a x-over with either a BLUE or GREEN badge person - the "network"
        //   - get their "present" value for this Role - have they been shortlisted or not (return all of them so the user can re-shortlist them if they  were removed by mistake)
        //   - get the job relevancy score for each candidate
        // my be able to answer questions like "given person a who are they connected to" so we can highlight their paths in the network when clicked
        // CandidateRankedDto is MISSING the raw job relevancy score which we need because a normalised score doesn't make sense when you are filtering
        //  by job relevancy

        if (!networkViewRef.current) {
            // console.log('RoleNetworkView networkViewRef.current not available')
            return
        }
        if (!sdManager) {
            // console.log('RoleNetworkView sdManager not available')
            return
        }

        console.log('Effect triggered', {
            candidatesLength: candidates.length,
            previousCandidatesLength: previousCandidatesLengthRef.current,
            time: new Date().toISOString(),
        })

        // only do this set up once
        if (!networkViewInitialised.current) {
            sdManager.setCaches(personLiteCache, companyCache)

            // set up event listeners
            sdManager.registerOnPersonSelected((person, personType) => {
                if (person) {
                    if (personType === PersonType.CANDIDATE) {
                        selectCandidateId(person.id)
                        setRightPaneMode('candidate')
                    } else {
                        setRightPaneMode('network_seed')
                    }
                }
            })

            sdManager.registerOnPersonHovered((person, personType) => {
                if (person) {
                    if (personType === PersonType.CANDIDATE) {
                        selectCandidateId(person.id)
                        setRightPaneMode('candidate')
                    } else {
                        setRightPaneMode('network_seed')
                    }
                }
            })

            // listen to the candidate changed event from the state store
            onCandidateChanged((updatedCandidate) => {
                sdManager.candidateChanged(updatedCandidate)
            })

            networkViewInitialised.current = true
        }

        // everything is now set up - has the candidate list changed?

        if (
            candidates.length === 0 &&
            previousCandidatesLengthRef.current > 0
        ) {
            console.log(
                `candidate length is ZERO - reset the network view. previousCandidatesLengthRef.current: ${previousCandidatesLengthRef.current}`,
            )
            previousCandidatesLengthRef.current = 0

            sdManager.reset()
        } else if (
            candidates.length > 0 &&
            previousCandidatesLengthRef.current === 0
        ) {
            console.log(
                `candidate length is NOT ZERO ${candidates.length} - load the network view previousCandidatesLengthRef.current: ${previousCandidatesLengthRef.current}`,
            )
            previousCandidatesLengthRef.current = candidates.length

            const role = safeGetRoleLite()

            const ephemeralPersons =
                personLiteCache.getManyOrThrow(ephemeralSeedIds)

            sdManager.initialiseSeedPersons(
                personLiteCache.getManyOrThrow(role.hiringTeamIds!),
                [
                    ...personLiteCache.getManyOrThrow(role.networkLinkIds!),
                    ...ephemeralPersons,
                ],
            )
            sdManager.addCandidates(candidates)
        }
    }, [
        candidates,
        personLiteCache,
        companyCache,
        safeGetRoleLite,
        selectCandidateId,
        setRightPaneMode,
        sdManager,
        onCandidateChanged,
        ephemeralSeedIds,
    ])

    return (
        <div className="">
            <NetworkView ref={handleNetworkViewRef} />
        </div>
    )
}

export default RoleNetworkView
